import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getPosterSignedUrl, registerPosterDoc, getPosterDoc, deletePosterDoc, deletePoster, getPosterInfo, updatePosterShareSetting, suggestRelatedDocs, assignCommentPhaseTags, createDocAssociation, listDocAssociations, deleteDocAssociation, getPhaseTagUsageStatus } from '../firebase/api';
import { useNavigate, useLocation } from 'react-router-dom';
import './PosterDetail.css';
import UserHeader from './UserHeader';
import { useAuth } from '../firebase/AuthContext';
import { clearPosterListScrollPosition } from '../utils/scrollNavigation';
import { trackCommentCreation } from '../utils/analytics';

/** コメント最下部に表示する更新日時（無ければ作成日時） */
const formatCommentDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

/** コメント一覧を作成日時の昇順に並べ替える（Firestoreのクエリ結果は順序保証がないため） */
const sortCommentsByCreatedAt = (list) => {
  return [...list].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return ta - tb;
  });
};

/** 前回の思考フェーズ取得以降、コメントが更新されていなければ再取得は不要と判定する */
const isPhaseTagsUpToDate = (comment) => {
  if (!comment?.phase_tags_updated_at) return false;
  const fetchedAt = new Date(comment.phase_tags_updated_at).getTime();
  if (Number.isNaN(fetchedAt)) return false;
  const commentUpdatedAt = new Date(comment.updated_at || comment.created_at || 0).getTime();
  if (Number.isNaN(commentUpdatedAt)) return true;
  return fetchedAt >= commentUpdatedAt;
};

const PosterDetail = ({ isMinimalView = false, poster: externalPoster = null }) => {
  const COMMENT_SUGGEST_SNIPPET_MAX = 90;
  /** 純粋ベクター検索のみ（評価用）。REACT_APP_SUGGEST_VECTOR_ONLY=true で有効 */
  const SUGGEST_VECTOR_ONLY = process.env.REACT_APP_SUGGEST_VECTOR_ONLY === 'true';
  const SUGGEST_RANKING_ROUTES_WITH_SIMILARITY = ['vector', 'vector_only'];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userProfile, logout, handleSessionExpired, isSessionExpiredError, sessionId } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNotMyPoster, setIsNotMyPoster] = useState(false);

  // 複数コメント管理（タグも含む）
  const [comments, setComments] = useState([]);
  const [activeCommentIndex, setActiveCommentIndex] = useState(0);
  const [isEditingActiveComment, setIsEditingActiveComment] = useState(false);
  
  // 支払いステータス管理
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);

  // 編集用の一時データ（タグも含む）
  const [tempComment, setTempComment] = useState({
    title: '',
    text: '',
    tags: []
  });
  const [tempTagsText, setTempTagsText] = useState('');

  // ポスター情報（location.stateから取得）
  const [posterInfo, setPosterInfo] = useState({
    imageUrl: '',
    question: '',
    title: '',
    signedUrl: '', // 署名付きURL
    authorIconUrl: '', // 作者アイコンURL
    authorName: '', // 作者名
    phase_tags: [], // 思考フェーズタグ
  });

  // 作成完了メッセージの表示制御
  const [showCreatedMessage, setShowCreatedMessage] = useState(false);
  const [messageVisible, setMessageVisible] = useState(false);

  // モーダルメッセージ用
  const [modal, setModal] = useState({
    show: false,
    message: '',
    type: '',
    onConfirm: null,
    onCancel: null,
    price: null,
  });

  // モーダル表示関数
  const showModal = (message, type = 'info', onConfirm = null, onCancel = null, options = {}) => {
    setModal({
      show: true,
      message,
      type,
      onConfirm,
      onCancel,
      price: options.price ?? null,
    });
  };
  const closeModal = () => {
    setModal({
      show: false,
      message: '',
      type: '',
      onConfirm: null,
      onCancel: null,
      price: null,
    });
  };

  // コメント下 suggest：関連コメント提案モーダル
  const [commentSuggestModalOpen, setCommentSuggestModalOpen] = useState(false);
  const [commentSuggestLoading, setCommentSuggestLoading] = useState(false);
  const [commentSuggestTab, setCommentSuggestTab] = useState('own');
  const [commentSuggestPage, setCommentSuggestPage] = useState(1);
  const [commentSuggestPayload, setCommentSuggestPayload] = useState({
    items: [],
    total: 0,
    total_pages: 1,
    page: 1,
    page_size: 4,
    ranking_route: 'heuristic',
  });
  const [commentSuggestDetailPoster, setCommentSuggestDetailPoster] = useState(null);

  /** source_doc_id -> 関連コメント行（自分のポスター限定・API が owner を検証） */
  const [commentAssociationsBySource, setCommentAssociationsBySource] = useState({});
  const [phaseTagsLoading, setPhaseTagsLoading] = useState(false);

  const reloadCommentAssociations = useCallback(async () => {
    if (externalPoster) {
      setCommentAssociationsBySource({});
      return;
    }
    const posterId = location.state?.posterId || location.state?.id || '';
    if (!sessionId || !posterId) {
      setCommentAssociationsBySource({});
      return;
    }
    try {
      const data = await listDocAssociations(sessionId, { poster_id: posterId });
      const rows = Array.isArray(data.items) ? data.items : [];
      const bySrc = {};
      rows.forEach((row) => {
        const sid = row.source_doc_id;
        if (!sid) return;
        if (!bySrc[sid]) bySrc[sid] = [];
        bySrc[sid].push(row);
      });
      setCommentAssociationsBySource(bySrc);
    } catch (err) {
      console.error('関連コメント一覧の取得に失敗:', err);
      setCommentAssociationsBySource({});
    }
  }, [sessionId, externalPoster, location.state?.posterId, location.state?.id]);

  useEffect(() => {
    reloadCommentAssociations();
  }, [reloadCommentAssociations]);

  const buildExternalPosterFromGetPoster = (data) => ({
    posterId: data.posterId,
    imageUrl: data.imageUrl || '',
    question: data.question || '',
    title: [data.title, data.title2, data.title3].filter(Boolean).join(''),
    authorIconUrl: data.authorIconUrl || '',
    authorName: data.authorName || '',
    phase_tags: data.phase_tags || [],
    authorId: data.authorId,
  });

  const truncateSuggestSnippet = (text) => {
    const raw = (text || '').replace(/\s+/g, ' ').trim();
    if (!raw) return '';
    if (raw.length <= COMMENT_SUGGEST_SNIPPET_MAX) return raw;
    return `${raw.slice(0, COMMENT_SUGGEST_SNIPPET_MAX)}・・・`;
  };

  const closeCommentSuggestModal = () => {
    setCommentSuggestModalOpen(false);
    setCommentSuggestLoading(false);
    setCommentSuggestDetailPoster(null);
    setCommentSuggestPayload({
      items: [],
      total: 0,
      total_pages: 1,
      page: 1,
      page_size: 4,
      ranking_route: 'heuristic',
    });
    setCommentSuggestPage(1);
    setCommentSuggestTab('own');
  };

  const loadCommentSuggestPage = async (tab, page, sourceDocIdLocal) => {
    const posterId = location.state?.posterId || location.state?.id || '';
    const srcId = sourceDocIdLocal || comments[activeCommentIndex]?.doc_id;
    if (!sessionId || !posterId || !srcId) return;
    setCommentSuggestLoading(true);
    try {
      const data = await suggestRelatedDocs(sessionId, {
        poster_id: posterId,
        source_doc_id: srcId,
        tab,
        page,
        page_size: 4,
        vector_only: SUGGEST_VECTOR_ONLY || undefined,
      });
      setCommentSuggestPayload({
        items: data.items || [],
        total: data.total ?? 0,
        total_pages: data.total_pages ?? 1,
        page: data.page ?? page,
        page_size: data.page_size ?? 4,
        ranking_route: data.ranking_route ?? 'heuristic',
      });
      setCommentSuggestPage(data.page ?? page);
    } catch (err) {
      console.error('関連コメント提案の取得に失敗:', err);
      showModal('関連コメントの取得に失敗しました。', 'info');
      setCommentSuggestPayload((prev) => ({ ...prev, items: [] }));
    } finally {
      setCommentSuggestLoading(false);
    }
  };

  /** 思考フェーズ取得アイコンのクリック起点。週の無料枠を表示した上で実行有無を確認する。 */
  const confirmFetchCommentPhaseTags = async () => {
    const cur = comments[activeCommentIndex];
    if (!sessionId) {
      showModal('ログインが必要です', 'info');
      return;
    }
    if (!cur?.doc_id) {
      showModal('コメントを登録すると思考フェーズを取得できます。', 'info');
      return;
    }
    let confirmMessage = '思考フェーズを取得しますか？';
    try {
      const usage = await getPhaseTagUsageStatus(sessionId);
      if (usage && typeof usage.week_limit === 'number') {
        confirmMessage =
          `週${usage.week_limit}回まで無料で手動実行できます（残り${Math.max(0, usage.week_remaining ?? 0)}回）。\n` +
          '思考フェーズを取得しますか？';
      }
    } catch (err) {
      console.error('思考フェーズ利用状況の取得に失敗:', err);
    }
    showModal(confirmMessage, 'confirm', () => {
      closeModal();
      handleFetchCommentPhaseTags();
    }, closeModal);
  };

  const handleFetchCommentPhaseTags = async () => {
    const cur = comments[activeCommentIndex];
    if (!sessionId) {
      showModal('ログインが必要です', 'info');
      return;
    }
    if (!cur?.doc_id) {
      showModal('コメントを登録すると思考フェーズを取得できます。', 'info');
      return;
    }
    const posterId = location.state?.posterId || location.state?.id || '';
    if (!posterId) {
      showModal('ポスターIDが取得できませんでした', 'info');
      return;
    }
    setPhaseTagsLoading(true);
    try {
      const data = await assignCommentPhaseTags(sessionId, {
        poster_id: posterId,
        doc_id: cur.doc_id,
      });
      const phaseTags = Array.isArray(data.phase_tags) ? data.phase_tags : [];
      setComments((prev) => {
        const next = [...prev];
        if (next[activeCommentIndex]) {
          next[activeCommentIndex] = {
            ...next[activeCommentIndex],
            phase_tags: phaseTags,
            // サーバー側の comment_phase_tags_updated_at 反映を待たず、取得直後にアイコンを非活性化する
            phase_tags_updated_at: new Date().toISOString(),
          };
        }
        return next;
      });
      if (phaseTags.length === 0) {
        showModal('該当する思考フェーズが見つかりませんでした', 'info');
      }
    } catch (err) {
      if (err.paymentRequired) {
        const usage = err.usage || {};
        const price = usage.price_yen || 500;
        showModal(
          usage.message || '無料枠を超えました',
          'payment',
          () => {
            closeModal();
            showPhaseTagPaymentUI();
          },
          closeModal,
          { price }
        );
        return;
      }
      console.error('思考フェーズ取得失敗:', err);
      showModal('思考フェーズの取得に失敗しました', 'info');
      if (isSessionExpiredError(err)) {
        handleSessionExpired();
      }
    } finally {
      setPhaseTagsLoading(false);
    }
  };

  const openCommentSuggestModal = async () => {
    const cur = comments[activeCommentIndex];
    if (!sessionId) {
      showModal('ログインが必要です', 'info');
      return;
    }
    if (!cur?.doc_id) {
      showModal('コメントを登録すると関連提案が利用できます。', 'info');
      return;
    }
    if (!Array.isArray(cur?.phase_tags) || cur.phase_tags.length === 0) {
      showModal('思考フェーズを取得すると関連コメントを提案できます。', 'info');
      return;
    }
    const posterId = location.state?.posterId || location.state?.id || '';
    if (!posterId) {
      showModal('ポスターIDが取得できませんでした', 'info');
      return;
    }
    setCommentSuggestModalOpen(true);
    setCommentSuggestDetailPoster(null);
    setCommentSuggestTab('own');
    setCommentSuggestPage(1);
    await loadCommentSuggestPage('own', 1, cur.doc_id);
  };

  const handleCommentSuggestSwitchTab = async (tab) => {
    if (tab === commentSuggestTab) return;
    setCommentSuggestTab(tab);
    setCommentSuggestDetailPoster(null);
    setCommentSuggestPage(1);
    const cur = comments[activeCommentIndex];
    await loadCommentSuggestPage(tab, 1, cur?.doc_id);
  };

  const handleCommentSuggestPageChange = async (nextPage) => {
    const total = commentSuggestPayload.total_pages || 1;
    const p = Math.min(Math.max(1, nextPage), total);
    if (p === commentSuggestPage && !commentSuggestLoading) return;
    setCommentSuggestPage(p);
    await loadCommentSuggestPage(commentSuggestTab, p, comments[activeCommentIndex]?.doc_id);
  };

  const handleCreateCommentAssociation = async (item) => {
    if (!item?.doc_id || item.already_linked) return;
    const posterId = location.state?.posterId || location.state?.id || '';
    const sourceDocId = comments[activeCommentIndex]?.doc_id;
    if (!sessionId || !posterId || !sourceDocId) return;
    const kind = commentSuggestTab === 'own' ? 'own_link' : 'other_favorite';
    try {
      await createDocAssociation(sessionId, {
        poster_id: posterId,
        source_doc_id: sourceDocId,
        target_doc_id: item.doc_id,
        kind,
      });
      showModal('関連付けました', 'info');
      setCommentSuggestPayload((prev) => ({
        ...prev,
        items: prev.items.map((row) =>
          row.doc_id === item.doc_id ? { ...row, already_linked: true } : row
        ),
      }));
      reloadCommentAssociations();
    } catch (err) {
      console.error(err);
      const msg = err.message && typeof err.message === 'string' ? err.message : '関連付けに失敗しました';
      showModal(msg, 'info');
    }
  };

  const navigateToRelatedCommentTarget = async (row) => {
    if (!sessionId || !row?.target_poster_id || !row?.target_doc_id) return;
    try {
      const info = await getPosterInfo(sessionId, row.target_poster_id);
      navigate('/poster-detail', {
        state: {
          posterId: info.posterId,
          posterImageUrl: info.imageUrl,
          title: [info.title, info.title2, info.title3].filter(Boolean).join(''),
          authorIconUrl: info.authorIconUrl || '',
          authorName: info.authorName || '',
          authorId: info.authorId,
          question: info.question || '',
          phase_tags: info.phase_tags || [],
          focusCommentDocId: row.target_doc_id,
        },
      });
    } catch (err) {
      console.error(err);
      showModal('関連先のポスターを開けませんでした', 'info');
    }
  };

  const handleDeleteCommentAssociation = (associationId) => {
    if (!associationId || !sessionId) return;
    showModal(
      '関連を解除しますか？',
      'confirm',
      async () => {
        closeModal();
        try {
          await deleteDocAssociation(sessionId, { association_id: associationId });
          await reloadCommentAssociations();
          showModal('関連を解除しました', 'info');
        } catch (err) {
          console.error(err);
          const msg = err.message && typeof err.message === 'string'
            ? err.message
            : '関連解除に失敗しました';
          showModal(msg, 'info');
        }
      },
      closeModal
    );
  };

  const openCommentSuggestPosterDetail = async (posterIdForRow, targetCommentDocId = null) => {
    if (!sessionId || !posterIdForRow) return;
    try {
      const info = await getPosterInfo(sessionId, posterIdForRow);
      setCommentSuggestDetailPoster({
        ...buildExternalPosterFromGetPoster(info),
        ...(targetCommentDocId ? { focusCommentDocId: targetCommentDocId } : {}),
      });
    } catch (err) {
      console.error(err);
      showModal('ポスター情報の取得に失敗しました', 'info');
    }
  };

  // 支払いステータスをチェックする関数
  const checkPremiumStatus = async () => {
    try {
      if (!sessionId) return;
      
      const { checkPaymentStatus } = await import('../firebase/api');
      const result = await checkPaymentStatus(sessionId);
      setHasPremiumAccess(result.is_paid);
      // Premium status checked
    } catch (error) {
      console.error('Premium status check failed:', error);
      setHasPremiumAccess(false);
      
      // 401エラー（セッション切れ）の場合はログイン画面に遷移
      if (isSessionExpiredError(error)) {
        handleSessionExpired();
      }
    }
  };

  useEffect(() => {
    // If an external poster is provided (suggest modal -> detail), use it instead of location.state
    if (externalPoster) {
      setIsLoading(true);
      // Set poster info directly from external poster
      setPosterInfo(prev => ({
        ...prev,
        imageUrl: externalPoster.imageUrl || '',
        question: externalPoster.question || '',
        title: externalPoster.title || '',
        authorIconUrl: externalPoster.authorIconUrl || '',
        authorName: externalPoster.authorName || '',
        phase_tags: externalPoster.phase_tags || [],
      }));
      // Always treat suggested poster as not my poster (hide edit/delete)
      setIsNotMyPoster(true);

      // Fetch comments for the suggested poster by its posterId if available
      const pid = externalPoster.posterId || '';
      if (pid) {
        if (!sessionId) {
          setComments([]);
          setIsLoading(false);
        } else {
          getPosterDoc(sessionId, pid)
          .then(docs => {
            if (Array.isArray(docs) && docs.length > 0) {
              const mappedComments = sortCommentsByCreatedAt(docs.map(doc => ({
                doc_id: doc.doc_id,
                title: doc.title || '',
                text: doc.comment || '',
                tags: doc.tags || [],
                phase_tags: Array.isArray(doc.phase_tags) ? doc.phase_tags : [],
                created_at: doc.created_at || '',
                updated_at: doc.updated_at || '',
                answered_question: doc.answered_question || '',
                source_question: doc.source_question || '',
                phase_tags_updated_at: doc.phase_tags_updated_at || ''
              })));
              setComments(mappedComments);
              const focusId = externalPoster.focusCommentDocId;
              // フォーカス対象の指定が無い場合は最も直近登録したコメント（末尾）をアクティブにする
              let nextIdx = mappedComments.length - 1;
              if (focusId) {
                const found = mappedComments.findIndex((c) => c.doc_id === focusId);
                if (found >= 0) nextIdx = found;
              }
              setActiveCommentIndex(nextIdx);
              setIsEditingActiveComment(false);
            } else if (docs && (docs.title || docs.comment)) {
              setComments([{ doc_id: docs.doc_id, title: docs.title || '', text: docs.comment || '', tags: docs.tags || [], phase_tags: Array.isArray(docs.phase_tags) ? docs.phase_tags : [], created_at: docs.created_at || '', updated_at: docs.updated_at || '', answered_question: docs.answered_question || '', source_question: docs.source_question || '', phase_tags_updated_at: docs.phase_tags_updated_at || '' }]);
              setActiveCommentIndex(0);
              setIsEditingActiveComment(false);
            } else {
              setComments([]);
            }
            setIsLoading(false);
          })
          .catch(err => {
            console.error('投稿情報取得失敗:', err);
            setComments([]);
            setIsLoading(false);
            
            // 401エラー（セッション切れ）の場合はログイン画面に遷移
            if (isSessionExpiredError(err)) {
              handleSessionExpired();
            }
          });
        }
      } else {
        setIsLoading(false);
      }
      return; // Skip the rest of the effect when external poster is used
    }

    // PosterCreateから遷移してきた場合のみメッセージを表示
    if (location.state && location.state.fromPosterCreate === true) {
      setShowCreatedMessage(true);
      setTimeout(() => { setMessageVisible(true); }, 100);
      setTimeout(() => { setMessageVisible(false); }, 3000);
      setTimeout(() => { setShowCreatedMessage(false); }, 3500);
    }

    // ローディング開始
    setIsLoading(true);

    if (location.state) {
      let imagePath = '';
      if (location.state.posterImageUrl) {
        imagePath = location.state.posterImageUrl;
      } else if (location.state.imageUrl) {
        imagePath = location.state.imageUrl;
      }
      console.info('画像パス:', imagePath);
      console.info('作成者:', location.state.authorIconUrl);
            setPosterInfo(prev => ({
              ...prev,
              imageUrl: imagePath,
              question: location.state.question || prev.question,
              title: location.state.title || prev.title,
              authorIconUrl: location.state.authorIconUrl || '',
              authorName: location.state.authorName || '',
              phase_tags: location.state.phase_tags || [],
            }));

      // 自分のポスターかどうか判定（authorIdがある場合はverifySessionでbackend user.idと比較）
      if (location.state.authorId) {
        if (!sessionId) {
          setIsNotMyPoster(true);
        } else {
        // verifySession APIを呼び出してbackend user.idを取得
        import('../firebase/api').then(api => {
          api.verifySession(sessionId)
            .then(user => {
              // backend user.idとauthorIdを比較
              setIsNotMyPoster(location.state.authorId !== user.firebase_uid);
              // Author ID and User ID comparison completed
            })
            .catch(err => {
              console.error('セッション確認失敗:', err);
              // 取得失敗時は編集不可
              setIsNotMyPoster(true);
              
              // 401エラー（セッション切れ）の場合はログイン画面に遷移
              if (isSessionExpiredError(err)) {
                handleSessionExpired();
              }
            });
        });
        }
      } else {
        // authorIdがない場合は従来通りアイコンURLで判定
        setIsNotMyPoster(
          location.state.authorIconUrl &&
          userProfile &&
          location.state.authorIconUrl !== (userProfile.iconURL || userProfile.photoURL)
        );
      }

      // 画像パスがあれば署名付きURL取得
      if (imagePath && !imagePath.startsWith('/img/') && sessionId) {
        let blobPath = imagePath;
        if (imagePath.startsWith('http')) {
          const match = imagePath.match(/\/preview\/poster\/(.+)$/);
          if (match) {
            blobPath = `preview/poster/${match[1]}`;
          }
        }
        getPosterSignedUrl(sessionId, blobPath)
          .then(url => {
            setPosterInfo(prev => ({ ...prev, signedUrl: url }));
          })
          .catch(err => {
            console.error('署名付きURL取得失敗:', err);
            
            // 401エラー（セッション切れ）の場合はログイン画面に遷移
            if (isSessionExpiredError(err)) {
              handleSessionExpired();
            }
          });
      }

      // posterId取得（location.state.posterId優先、なければlocation.state.id）
      const posterId = location.state.posterId || location.state.id || '';
      if (posterId) {
        if (!sessionId) {
          setIsLoading(false);
        } else {
        // バックエンドからポスター情報を取得
        if (posterId) {
          getPosterInfo(sessionId, posterId)
            .then(posterData => {
              setPosterInfo(prev => ({
                ...prev,
                question: posterData.question || prev.question || '',
                title: posterData.title || prev.title || '',
                authorIconUrl: posterData.authorIconUrl || prev.authorIconUrl || '',
                authorName: posterData.authorName || prev.authorName || '',
                phase_tags: posterData.phase_tags || prev.phase_tags || [],
              }));
            })
            .catch(err => {
              console.error('ポスター情報取得失敗:', err);
              
              // 401エラー（セッション切れ）の場合はログイン画面に遷移
              if (isSessionExpiredError(err)) {
                handleSessionExpired();
              }
            });
        }
        
        // 投稿情報取得（Firestore）
        getPosterDoc(sessionId, posterId)
          .then(docs => {
            // 複数ドキュメント対応: 配列で返却される前提
            if (Array.isArray(docs) && docs.length > 0) {
              const mappedComments = sortCommentsByCreatedAt(docs.map(doc => ({
                doc_id: doc.doc_id,
                title: doc.title || '',
                text: doc.comment || '',
                tags: doc.tags || [],
                phase_tags: Array.isArray(doc.phase_tags) ? doc.phase_tags : [],
                created_at: doc.created_at || '',
                updated_at: doc.updated_at || '',
                answered_question: doc.answered_question || '',
                source_question: doc.source_question || '',
                phase_tags_updated_at: doc.phase_tags_updated_at || ''
              })));
              setComments(mappedComments);
              const focusId = location.state?.focusCommentDocId;
              // フォーカス対象の指定が無い場合は最も直近登録したコメント（末尾）をアクティブにする
              let nextIdx = mappedComments.length - 1;
              if (focusId) {
                const found = mappedComments.findIndex((c) => c.doc_id === focusId);
                if (found >= 0) nextIdx = found;
              }
              setActiveCommentIndex(nextIdx);
              setIsEditingActiveComment(false);
            } else if (docs && (docs.title || docs.comment)) {
              // 1件のみオブジェクトで返る場合も考慮
              const single = [{ doc_id: docs.doc_id, title: docs.title || '', text: docs.comment || '', tags: docs.tags || [], phase_tags: Array.isArray(docs.phase_tags) ? docs.phase_tags : [], created_at: docs.created_at || '', updated_at: docs.updated_at || '', answered_question: docs.answered_question || '', source_question: docs.source_question || '', phase_tags_updated_at: docs.phase_tags_updated_at || '' }];
              setComments(single);
              const focusIdSingle = location.state?.focusCommentDocId;
              let nextIdxSingle = 0;
              if (focusIdSingle) {
                const foundS = single.findIndex((c) => c.doc_id === focusIdSingle);
                if (foundS >= 0) nextIdxSingle = foundS;
              }
              setActiveCommentIndex(nextIdxSingle);
              setIsEditingActiveComment(false);
            } else {
              setComments([]);
            }
            setIsLoading(false);
          })
          .catch(err => {
            console.error('投稿情報取得失敗:', err);
            setComments([]);
            setIsLoading(false);
            
            // 401エラー（セッション切れ）の場合はログイン画面に遷移
            if (isSessionExpiredError(err)) {
              handleSessionExpired();
            }
          });
        }
      } else {
        setIsLoading(false);
      }
  
    } else {
      setIsLoading(false);
    }

    // 支払いステータスをチェック（常に実行）
    checkPremiumStatus();

    // 初期化時にコメントが空の場合は編集モードで開始
    if (comments.length === 0) {
      setIsEditingActiveComment(true);
    }
  }, [location.state, externalPoster, sessionId]);

  // コメント編集の開始
  const startEditingComment = () => {
    const currentComment = comments[activeCommentIndex] || { title: '', text: '', tags: [] };
    setTempComment(currentComment);
    setTempTagsText(currentComment.tags.join(', '));
    setIsEditingActiveComment(true);
  };

  // コメント編集のキャンセル
  const cancelEditingComment = () => {
    const currentComment = comments[activeCommentIndex] || { title: '', text: '', tags: [] };
    setTempComment(currentComment);
    setTempTagsText(currentComment.tags.join(', '));
    setIsEditingActiveComment(false);
    
    // 新規追加中で内容が空の場合、そのコメントを削除
    if (activeCommentIndex >= 0 && comments[activeCommentIndex] && 
        !comments[activeCommentIndex].title && !comments[activeCommentIndex].text) {
      const newComments = comments.filter((_, index) => index !== activeCommentIndex);
      setComments(newComments);
      setActiveCommentIndex(Math.max(0, activeCommentIndex - 1));
    }
  };

  // コメント編集の保存
  const saveComment = () => {
    if (!tempComment.title.trim() && !tempComment.text.trim()) {
      // 空のコメントの場合、編集モードを終了するだけ
      setIsEditingActiveComment(false);
      
      // 新規追加中で内容が空の場合、そのコメントを削除
      if (activeCommentIndex >= 0 && comments[activeCommentIndex] && 
          !comments[activeCommentIndex].title && !comments[activeCommentIndex].text) {
        const newComments = comments.filter((_, index) => index !== activeCommentIndex);
        setComments(newComments);
        setActiveCommentIndex(Math.max(0, activeCommentIndex - 1));
      }
      return;
    }

    // タグをパース
    const tags = tempTagsText.split(',').map(tag => tag.trim()).filter(tag => tag);

    const commentWithTags = {
      ...tempComment,
      tags: tags
    };

    const newComments = [...comments];
    newComments[activeCommentIndex] = commentWithTags;
    setComments(newComments);
    setIsEditingActiveComment(false);
  };

  // コメント削除処理
  const handleDeleteComment = (index) => {
    const comment = comments[index];
    if (!comment || !comment.doc_id) {
      showModal('削除できるコメントがありません', 'info');
      return;
    }
    showModal('コメントを削除します。よろしいですか？', 'confirm', async () => {
      closeModal();
      const posterId = location.state?.posterId || location.state?.id || '';
      if (!posterId) {
        showModal('ポスターIDが取得できませんでした', 'info');
        return;
      }
      if (!sessionId) {
        showModal('セッションが無効です。再度ログインしてください。', 'info');
        return;
      }
      try {
        const res = await deletePosterDoc(sessionId, posterId, comment.doc_id);
        if (res.success) {
          // コメント再取得
          try {
            const docs = await getPosterDoc(sessionId, posterId);
            if (Array.isArray(docs) && docs.length > 0) {
              const mappedComments = sortCommentsByCreatedAt(docs.map(doc => ({
                doc_id: doc.doc_id,
                title: doc.title || '',
                text: doc.comment || '',
                tags: doc.tags || [],
                phase_tags: Array.isArray(doc.phase_tags) ? doc.phase_tags : [],
                created_at: doc.created_at || '',
                updated_at: doc.updated_at || '',
                answered_question: doc.answered_question || '',
                source_question: doc.source_question || '',
                phase_tags_updated_at: doc.phase_tags_updated_at || ''
              })));
              setComments(mappedComments);
              // 削除後は最も直近登録したコメント（末尾）をアクティブにする
              setActiveCommentIndex(mappedComments.length - 1);
            } else {
              setComments([]);
            }
            reloadCommentAssociations();
            showModal('コメントを削除しました', 'info');
          } catch (err) {
            console.error('コメント再取得失敗:', err);
            showModal('削除は完了しましたが、最新情報の取得に失敗しました', 'info');
            
            // 401エラー（セッション切れ）の場合はログイン画面に遷移
            if (isSessionExpiredError(err)) {
              handleSessionExpired();
            }
          }
        } else {
          showModal('削除に失敗しました: ' + res.message, 'info');
        }
      } catch (err) {
        console.error('コメント削除失敗:', err);
        showModal('削除に失敗しました', 'info');
        
        // 401エラー（セッション切れ）の場合はログイン画面に遷移
        if (isSessionExpiredError(err)) {
          handleSessionExpired();
        }
      }
    }, closeModal);
  };

  // 新しいコメントタブを追加
  const addNewComment = () => {
    // 3つ目までは無料、4つ目以降はプレミアム機能
    if (comments.length >= 3 && !hasPremiumAccess) {
      showPaymentUI();
      return;
    }
    
    // プレミアムユーザーでも最大20個まで
    if (comments.length >= 14) {
      showModal('コメントは最大13個まで追加できます', 'info');
      return;
    }
    
    const newComments = [...comments, { title: '', text: '', tags: [], phase_tags: [] }];
    setComments(newComments);
    setActiveCommentIndex(newComments.length - 1);
    setTempComment({ title: '', text: '', tags: [] });
    setTempTagsText('');
    setIsEditingActiveComment(true);
  };

  // 現在アクティブなコメントが「元になっている」最新の問いかけ文を返す。
  // location.state.linkedQuestionText はページ全体で1つしか持てないナビゲーション時の値なので、
  // 実際にその遷移対象だったコメント（focusCommentDocId）の時だけフォールバックとして使う。
  // そうしないと、タブを切り替えた別のコメントにも同じ問いかけが漏れて表示されてしまう。
  const getActiveSourceQuestion = () => {
    const active = comments[activeCommentIndex];
    if (!active) return '';
    if (active.source_question) return active.source_question;
    if (active.doc_id && active.doc_id === location.state?.focusCommentDocId) {
      return location.state?.linkedQuestionText || '';
    }
    return '';
  };

  // 問いかけに答えるための新しいコメントタブを追加（問いかけ文を引き継ぐ）
  const handleAnswerQuestion = () => {
    const questionText = getActiveSourceQuestion();

    // 3つ目までは無料、4つ目以降はプレミアム機能
    if (comments.length >= 3 && !hasPremiumAccess) {
      showPaymentUI();
      return;
    }

    // プレミアムユーザーでも最大20個まで
    if (comments.length >= 14) {
      showModal('コメントは最大13個まで追加できます', 'info');
      return;
    }

    const newComments = [
      ...comments,
      {
        title: '',
        text: '',
        tags: [],
        phase_tags: [],
        answered_question: questionText,
      },
    ];
    setComments(newComments);
    setActiveCommentIndex(newComments.length - 1);
    setTempComment({
      title: '',
      text: '',
      tags: [],
      answered_question: questionText,
    });
    setTempTagsText('');
    setIsEditingActiveComment(true);
  };

  // 課金UIを表示（Stripe Checkout）
  const showPaymentUI = async () => {
    try {
      if (!sessionId) {
        showModal('セッションが無効です。再度ログインしてください。', 'info');
        return;
      }
      // バックエンドでCheckout Sessionを作成
      const { createCheckoutSession } = await import('../firebase/api');
      
      // posterImageUrlから署名付きトークンを除去してベースURLのみ抽出
      const fullImageUrl = location.state?.posterImageUrl || '';
      const baseImageUrl = fullImageUrl.split('?')[0]; // ?以降のクエリパラメータを除去
      
      const checkoutData = await createCheckoutSession(sessionId, {
        userId: userProfile?.uid,
        posterImageUrl: baseImageUrl,
        question: location.state?.question,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&poster_image_url=${encodeURIComponent(baseImageUrl)}&poster_id=${location.state?.posterId}`,
        cancelUrl: `${window.location.origin}/poster-list`
      });
      
      // Stripe.jsを読み込んでCheckoutページにリダイレクト
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe('pk_live_51S770FRyfyfP7e06hbcyWAb7pQbz5yl41e9RnmrYALA4wz7FxHUMvFHfRX9Y9lpGgznKYj5Q8XTEjXbMkzRFiklr006Rvh4Qs6');
      
      const result = await stripe.redirectToCheckout({
        sessionId: checkoutData.sessionId
      });
      
      if (result.error) {
        console.error('Checkout error:', result.error);
        showModal('決済の開始に失敗しました', 'info');
      }
    } catch (error) {
      console.error('Payment setup error:', error);
      showModal('決済システムの読み込みに失敗しました', 'info');
    }
  };

  const showPhaseTagPaymentUI = async () => {
    try {
      if (!sessionId) {
        showModal('セッションが無効です。再度ログインしてください。', 'info');
        return;
      }
      const { createCheckoutSession } = await import('../firebase/api');
      const posterId = location.state?.posterId || location.state?.id || '';
      const fullImageUrl = location.state?.posterImageUrl || '';
      const baseImageUrl = fullImageUrl.split('?')[0];
      const checkoutData = await createCheckoutSession(sessionId, {
        userId: userProfile?.uid,
        posterImageUrl: baseImageUrl,
        posterId,
        productType: 'phase_tag_credits',
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&poster_image_url=${encodeURIComponent(baseImageUrl)}&poster_id=${posterId}&product_type=phase_tag_credits`,
        cancelUrl: window.location.href,
      });
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe('pk_live_51S770FRyfyfP7e06hbcyWAb7pQbz5yl41e9RnmrYALA4wz7FxHUMvFHfRX9Y9lpGgznKYj5Q8XTEjXbMkzRFiklr006Rvh4Qs6');
      const result = await stripe.redirectToCheckout({
        sessionId: checkoutData.sessionId,
      });
      if (result.error) {
        console.error('Checkout error:', result.error);
        showModal('決済の開始に失敗しました', 'info');
      }
    } catch (error) {
      console.error('Phase tag payment setup error:', error);
      showModal('決済システムの読み込みに失敗しました', 'info');
    }
  };

  // コメントタブを切り替え
  const switchCommentTab = (index) => {
    setActiveCommentIndex(index);
    setIsEditingActiveComment(false);
  };

  // ポスター編集ボタン
  const handleEditPoster = () => {
    const posterId = location.state?.posterId || location.state?.id || '';
    clearPosterListScrollPosition();
    navigate('/poster-create', {
      state: {
        posterId,
        isEdit: true
      }
    });
  };

  const handleEditProfile = () => {
    navigate('/profile-setup', { state: { editMode: true } });
  };

  const handlePosterList = () => {
    navigate('/poster-list');
  };

  // ポスター共有処理
  const handleSharePoster = async (platform) => {
    try {
      const posterId = location.state?.posterId || location.state?.id || '';
      
      if (!posterId) {
        showModal('ポスターIDが取得できませんでした', 'info');
        return;
      }

      if (!sessionId) {
        showModal('セッションが無効です。再度ログインしてください。', 'info');
        return;
      }

      // ポスターのshare属性を'ok'に更新
      await updatePosterShareSetting(sessionId, posterId, 'ok');
      
      // 共有ページのURL
      const shareUrl = `https://app.tiny-identity.com/p/${posterId}`;
      const encUrl = encodeURIComponent(shareUrl);
      const encTitle = encodeURIComponent(location.state.title || 'ポスターをシェア');
      
      // プラットフォームに応じた共有URLを生成
      let shareTargetUrl = '';
      if (platform === 'twitter') {
        shareTargetUrl = `https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}&via=tinyidentity`;
      } else if (platform === 'facebook') {
        shareTargetUrl = `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`;
      }
      
      if (shareTargetUrl) {
        // 共有UIを新しいウィンドウで開く
        window.open(shareTargetUrl, '_blank', 'width=600,height=400');
      }
      
    } catch (error) {
      console.error('共有処理エラー:', error);
      showModal('共有設定の更新に失敗しました', 'info');
      
      // 401エラー（セッション切れ）の場合はログイン画面に遷移
      if (isSessionExpiredError(error)) {
        handleSessionExpired();
      }
    }
  };

  // ポスター削除処理
  const handleDeletePoster = () => {
    const posterId = location.state?.posterId || location.state?.id || '';
    if (!posterId) {
      showModal('ポスターIDが取得できませんでした', 'info');
      return;
    }
    if (!sessionId) {
      showModal('セッションが無効です。再度ログインしてください。', 'info');
      return;
    }
    let confirmMsg = '';
    if (comments.length > 0 && comments.some(c => c.title || c.text)) {
      confirmMsg = 'ポスターと共にメントも削除されますが良いですか？';
    } else {
      confirmMsg = 'ポスターを削除します。よろしいですか？';
    }
    showModal(confirmMsg, 'confirm', async () => {
      closeModal();
      try {
        const result = await deletePoster(sessionId, posterId);
        if (result) {
          // ポスター一覧関連のキャッシュをクリア
          localStorage.removeItem(`myPosters_${sessionId}`);
          localStorage.removeItem(`myPosters_${sessionId}_timestamp`);
          localStorage.removeItem(`favoritePosters_${sessionId}`);
          localStorage.removeItem(`favoritePosters_${sessionId}_timestamp`);
          
          showModal('ポスターを削除しました', 'info', () => {
            closeModal();
            clearPosterListScrollPosition();
            navigate('/top');
          });
        } else {
          showModal('削除に失敗しました', 'info');
        }
      } catch (err) {
        console.error('ポスター削除失敗:', err);
        showModal('削除に失敗しました', 'info');
        
        // 401エラー（セッション切れ）の場合はログイン画面に遷移
        if (isSessionExpiredError(err)) {
          handleSessionExpired();
        }
      }
    }, closeModal);
  };

  const handleRegisterComment = async () => {
    const posterId = location.state?.posterId || location.state?.id || '';
    if (!posterId) {
      showModal('ポスターIDが取得できませんでした', 'info');
      return;
    }
    if (!sessionId) {
      showModal('セッションが無効です。再度ログインしてください。', 'info');
      return;
    }
    const tags = tempTagsText.split(',').map(tag => tag.trim()).filter(tag => tag).slice(0, 7);
    const docId = comments[activeCommentIndex]?.doc_id || null;
    const payload = {
      poster_id: posterId,
      doc_id: docId,
      title: tempComment.title,
      comment: tempComment.text,
      tags: tags,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...(tempComment.answered_question ? { answered_question: tempComment.answered_question } : {})
    };
    try {
      // タイトルとコメントが空でないことを確認
      if (!tempComment.title.trim() || !tempComment.text.trim()) {
        showModal('タイトルとコメントは必須です', 'info');
        return;
      }

      const regResult = await registerPosterDoc(sessionId, payload);
      // 新規作成時はdocIdが未確定なので、レスポンスに含まれる作成後のdoc_idを使う
      const savedDocId = docId || regResult?.doc_id || null;
      // 新規作成時のみ計測（編集・再保存はカウントしない）。「あなたへの問いかけ」への回答かどうかも区別する
      if (!docId) {
        trackCommentCreation(savedDocId, Boolean(tempComment.answered_question));
      }

      const docs = await getPosterDoc(sessionId, posterId);
      if (Array.isArray(docs) && docs.length > 0) {
        const mappedComments = sortCommentsByCreatedAt(docs.map(doc => ({
          doc_id: doc.doc_id,
          title: doc.title || '',
          text: doc.comment || '',
          tags: doc.tags || [],
          phase_tags: Array.isArray(doc.phase_tags) ? doc.phase_tags : [],
          created_at: doc.created_at || '',
          updated_at: doc.updated_at || '',
          answered_question: doc.answered_question || '',
          source_question: doc.source_question || '',
          phase_tags_updated_at: doc.phase_tags_updated_at || ''
        })));
        setComments(mappedComments);
        // 今回登録・更新したコメントのタブをアクティブにする
        const savedIdx = savedDocId
          ? mappedComments.findIndex((c) => c.doc_id === savedDocId)
          : -1;
        setActiveCommentIndex(savedIdx >= 0 ? savedIdx : mappedComments.length - 1);
      }
      showModal('コメント・説明を登録しました', 'info', () => {
        setIsEditingActiveComment(false);
        closeModal();
      });
    } catch (err) {
      console.error('コメント登録失敗:', err);
      showModal('登録に失敗しました', 'info');
      
      // 401エラー（セッション切れ）の場合はログイン画面に遷移
      if (isSessionExpiredError(err)) {
        handleSessionExpired();
      }
    }
  };

  const handleMenuClick = (path) => {
    if (path !== '/poster-list' && path !== '/list') {
      clearPosterListScrollPosition();
    }
    navigate(path);
    setIsDropdownOpen(false);
  };

  const shareUrl = `https://app.tiny-identity.com/p/${location.state.posterId}`;
  const encUrl = encodeURIComponent(shareUrl);

  return (
    <div className="detail-container">
      {/* Render header only if not in minimal view */}
      {!isMinimalView && (
        <>
          <div className="detail-content">
            <nav className="detail-breadcrumb">
              <button 
                className="breadcrumb-link"
                onClick={() => {
                  clearPosterListScrollPosition();
                  navigate('/top');
                }}
              >
                TOP
              </button>
              <span className="breadcrumb-separator"> &gt; </span>
              <span className="breadcrumb-current">ポスター詳細</span>
            </nav>
          </div>

          <div className="top-header" style={{ position: 'relative', overflow: 'visible' }}>
            <div className="user-info">
              <img src={userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'} alt="プロフィール画像" title="プロフィール編集" onClick={handleEditProfile} style={{ cursor: 'pointer' }} />
              <span className="user-name">{userProfile?.displayName || userProfile?.nickname || 'ユーザー'} さん</span>
              {userProfile?.world && <span className="user-world">{userProfile.world}</span>}
            </div>
            <div className="header-icons">
                <img
                  src="/img/list.png"
                  alt="ポスター 一覧"
                  title="ポスター 一覧"
                  className="header-icon"
                  style={{ cursor: 'pointer' }}
                  onClick={handlePosterList}
                />
              <img
                src="/img/graph.png"
                alt="みんなのサマリー"
                title="みんなのサマリー"
                className="header-icon"
                onClick={() => navigate('/')}
                style={{ cursor: 'pointer', marginLeft: '10px' }}
              />
              <img 
                src="/img/logout.png" 
                alt="ログアウト" 
                title="ログアウト" 
                className="header-icon" 
                onClick={() => { if (window.confirm('ログアウトしますか？')) logout(); }}
                style={{ cursor: 'pointer', marginLeft: '10px' }} 
              />
            </div>
          </div>
        </>
      )}

      {/* Render the rest of the content */}
      <div className="detail-content">
        {/* モーダル表示（ヘッダー直下） */}
        {modal.show && (
          <div className="modal-overlay">
            {modal.type === 'payment' ? (
              <div className="modal-content modal-content-payment">
                <img src="/img/cat.png" alt="" className="modal-payment-icon" />
                <div className="modal-message-area">
                  <p className="modal-message modal-message--left">{modal.message}</p>
                </div>
                <div className="modal-actions modal-actions--payment">
                  <button
                    type="button"
                    className="modal-button-payment modal-button-payment--cancel"
                    onClick={modal.onCancel || closeModal}
                  >
                    キャンセル
                  </button>
                  <button
                    type="button"
                    className="modal-button-payment modal-button-payment--pay"
                    onClick={modal.onConfirm}
                  >
                    ¥ {modal.price || 500}
                  </button>
                </div>
              </div>
            ) : (
              <div className="modal-content modal-content-small">
                <div className="modal-message-area modal-message-area--compact">
                  <p className="modal-message modal-message--left">{modal.message}</p>
                </div>
                {modal.type === 'confirm' ? (
                  <div className="modal-actions">
                    <button
                      className="modal-button"
                      onClick={modal.onConfirm}
                    >
                      OK
                    </button>
                    <button
                      className="modal-button modal-button-cancel"
                      onClick={modal.onCancel || closeModal}
                    >
                      キャンセル
                    </button>
                  </div>
                ) : (
                  <div className="modal-actions modal-actions--single">
                    <button
                      className="modal-button"
                      onClick={modal.onConfirm || closeModal}
                    >
                      OK
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ローディング表示 */}
        {isLoading ? (
          <div className="detail-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">読み込み中...</div>
          </div>
        ) : (
          <React.Fragment>
            {/* コンテンツエリア */}
            <div className="detail-content">
              {/* 作成完了メッセージ */}
              {showCreatedMessage && (
                <div className={`detail-create-done ${messageVisible ? 'visible' : ''}`}>
                  ポスターが作成されました！
                </div>
              )}

              {/* 質問（空の場合は非表示） */}
              {posterInfo.question && posterInfo.question.trim() !== '' && (
                  <div className="detail-question-tab">
                    <img src="/img/cat-purple.png" alt="質問アイコン" />
                    <span>{posterInfo.question}</span>
                  </div>
              )}

              {/* ポスター表示 */}
              <div className="detail-poster-view" style={{ position: 'relative' }}>
                <img src={posterInfo.signedUrl || posterInfo.imageUrl} alt="ポスター画像" />
                {posterInfo.authorIconUrl && (
                  <div className="detail-author-icon-wrapper">
                    <img
                      src={posterInfo.authorIconUrl}
                      alt="作者アイコン"
                      className="detail-author-icon"
                      title={posterInfo.authorName || '作者'}
                    />
                    {/* ツールチップ */}
                    <div className="detail-author-tooltip">
                      {posterInfo.authorName || '作者'}
                    </div>
                  </div>
                )}
              </div>

              {/* アイコンバー＋削除ボタン */}
              <div className="detail-icon-bar">
                {/* 編集ボタン（削除ボタンの左横） */}
                {!isNotMyPoster && (
                  <>
                    <img 
                      src="/img/x-social-media.webp" 
                      alt="Xで共有" 
                      width="20px" 
                      height="20px"
                      style={{ cursor: 'pointer', marginRight: '18px', marginTop: '10px' }}
                      onClick={() => handleSharePoster('twitter')}
                      title="Xで共有"
                    />
                    <img 
                      src="/img/facebook.png" 
                      alt="Facebookで共有" 
                      width="20px" 
                      height="20px"
                      style={{ cursor: 'pointer', marginRight: '25px', marginTop: '10px' }}
                      onClick={() => handleSharePoster('facebook')}
                      title="Facebookで共有"
                    />
                    <img
                      src="/img/edit.png"
                      alt="ポスター編集"
                      className="detail-edit-poster-icon"
                      onClick={handleEditPoster}
                      title="ポスター編集"
                      style={{ marginRight: '12px', width: '20px', height: '20px', cursor: 'pointer', opacity: 0.85 }}
                    />
                    <img
                      src="/img/gomi.png"
                      alt="ポスター削除"
                      className="detail-delete-poster-icon"
                      onClick={handleDeletePoster}
                      title="ポスター削除"
                    />
                  </>
                )}
              </div>

              {/* コメントタブエリア */}
              <div className="detail-comment-tabs-container">
                {/* このタブが「問いかけの元になったコメント」の場合、タブ上部に最新の問いかけを表示
                    （過去の問いかけに答えたコメントが、新たな問いかけの元にもなり得るため、
                    answered_question の有無とは独立に判定する） */}
                {getActiveSourceQuestion() && (
                  <div className="detail-linked-question detail-linked-question--tabs">
                    <img src="/img/cat-purple.png" alt="" />
                    <span className="detail-linked-question-text">
                      {getActiveSourceQuestion()}
                    </span>
                    {!isNotMyPoster && (
                      <button
                        type="button"
                        className="detail-answer-question-btn"
                        onClick={handleAnswerQuestion}
                      >
                        答える
                      </button>
                    )}
                  </div>
                )}
                <div className="detail-comment-tabs">
                  {comments
                    .filter(comment => comment.title || comment.text) // 内容があるコメントのみ表示
                    .map((comment, filteredIndex) => {
                      // 元のインデックスを取得
                      const originalIndex = comments.findIndex(c => c === comment);
                      // タイトルが長い場合は...で省略
                      const tabTitle = comment.title || `コメント${filteredIndex + 1}`;
                      const maxTabLength = 7; // 最大文字数（必要に応じて調整）
                      const displayTitle = tabTitle.length > maxTabLength ? tabTitle.slice(0, maxTabLength) + '…' : tabTitle;
                      return (
                        <button
                          key={originalIndex}
                          className={`detail-comment-tab ${activeCommentIndex === originalIndex ? 'active' : ''}`}
                          onClick={() => switchCommentTab(originalIndex)}
                          title={tabTitle} // hover時に全体表示
                        >
                          {displayTitle}
                        </button>
                      );
                    })}
                  {/* 新規追加ボタン（プレミアムユーザーは最大20個、通常ユーザーは3個まで）- 自分のポスターのみ */}
                  {!isNotMyPoster && comments.filter(comment => comment.title || comment.text).length < (hasPremiumAccess ? 13 : 3) && (
                    <button 
                      className="detail-comment-tab add-tab"
                      onClick={addNewComment}
                    >
                      ＋
                    </button>
                  )}
                  {/* 4つ目のコメント用課金ボタン（プレミアムアクセスなしの場合のみ表示）- 自分のポスターのみ */}
                  {!isNotMyPoster && !hasPremiumAccess && comments.filter(comment => comment.title || comment.text).length === 3 && (
                    <button 
                      className="detail-comment-tab add-tab premium-tab"
                      onClick={showPaymentUI}
                      title="4つ目のコメントを追加するには課金が必要です"
                    >
                      ＋
                    </button>
                  )}
                </div>

                {/* コメント内容表示・編集エリア */}
                {(comments.length > 0 || isEditingActiveComment) && (
                  <div
                    className={
                      'detail-comment-content' +
                      (!isNotMyPoster &&
                      !isEditingActiveComment &&
                      comments[activeCommentIndex] &&
                      (comments[activeCommentIndex].title || comments[activeCommentIndex].text)
                        ? ' detail-comment-content--with-floating-icons'
                        : '')
                    }
                  >
                      {!isEditingActiveComment && comments[activeCommentIndex] &&
                       (comments[activeCommentIndex].title || comments[activeCommentIndex].text) && (
                        <React.Fragment>
                          <div className="detail-comment-label">
                            <div className="detail-comment-title-row">
                              <strong>{comments[activeCommentIndex].title}</strong>
                              {comments[activeCommentIndex].phase_tags &&
                                comments[activeCommentIndex].phase_tags.length > 0 && (
                                  <span className="detail-comment-phase-tag-images">
                                    {comments[activeCommentIndex].phase_tags.map((tagItem, idx) => {
                                      const tagKey =
                                        tagItem && typeof tagItem === 'object' && tagItem.tag
                                          ? tagItem.tag
                                          : typeof tagItem === 'string'
                                            ? tagItem
                                            : null;
                                      if (!tagKey) return null;
                                      return (
                                        <img
                                          key={`${comments[activeCommentIndex].doc_id || 'noid'}-${activeCommentIndex}-${idx}-${tagKey}`}
                                          src={`/img/phase-tags/${tagKey}.png`}
                                          alt={tagKey}
                                          title={tagKey}
                                          className="detail-comment-phase-tag-img"
                                          style={{ animationDelay: `${idx * 0.18}s` }}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      );
                                    })}
                                  </span>
                                )}
                            </div>
                          </div>
                          {/* このコメント自身が「問いかけへの回答」の場合、タイトル直下に回答対象の問いかけを表示（ボタンなし） */}
                          {comments[activeCommentIndex]?.answered_question && (
                            <div className="detail-linked-question detail-linked-question--inline">
                              <img src="/img/cat-purple.png" alt="" />
                              <span className="detail-linked-question-text">
                                {comments[activeCommentIndex].answered_question}
                              </span>
                            </div>
                          )}
                          <p className="detail-comment-text">{comments[activeCommentIndex].text}</p>
                          {/* タグ表示 */}
                          {comments[activeCommentIndex].tags && comments[activeCommentIndex].tags.length > 0 && (
                            <div className="detail-comment-tags">
                              <div className="detail-tags-list">
                                {comments[activeCommentIndex].tags.map((tag, index) => (
                                  <span key={index} className="detail-tag">{tag}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formatCommentDateTime(
                            comments[activeCommentIndex].updated_at || comments[activeCommentIndex].created_at
                          ) && (
                            <p className="detail-comment-datetime">
                              {formatCommentDateTime(
                                comments[activeCommentIndex].updated_at || comments[activeCommentIndex].created_at
                              )}
                            </p>
                          )}
                          {!isNotMyPoster &&
                            (
                              commentAssociationsBySource[comments[activeCommentIndex]?.doc_id] || []
                            ).length > 0 && (
                            <div className="detail-comment-related" aria-label="関連コメント">
                              <ul className="detail-comment-related-list">
                                {(
                                  commentAssociationsBySource[comments[activeCommentIndex].doc_id] || []
                                ).map((row) => {
                                  const phaseTags = Array.isArray(row.target_phase_tags)
                                    ? row.target_phase_tags
                                    : [];
                                  return (
                                    <li
                                      key={
                                        row.association_id
                                        || `${row.source_doc_id}-${row.target_doc_id}-${row.kind}`
                                      }
                                      className="detail-comment-related-item"
                                    >
                                      {row.target_author_icon_url ? (
                                        <img
                                          src={row.target_author_icon_url}
                                          alt=""
                                          className="detail-comment-related-avatar"
                                        />
                                      ) : null}
                                      <button
                                        type="button"
                                        className="detail-comment-related-title-btn"
                                        onClick={() => navigateToRelatedCommentTarget(row)}
                                      >
                                        {row.target_title}
                                      </button>
                                      <span className="detail-comment-related-phase-tags">
                                        {phaseTags.slice(0, 3).map((tagItem, idx) => {
                                          const tk =
                                            tagItem && typeof tagItem === 'object' && tagItem.tag
                                              ? tagItem.tag
                                              : typeof tagItem === 'string'
                                                ? tagItem
                                                : null;
                                          if (!tk) return null;
                                          return (
                                            <img
                                              key={`rel-ph-${row.target_doc_id}-${idx}-${tk}`}
                                              src={`/img/phase-tags/${tk}.png`}
                                              alt={tk}
                                              title={tk}
                                              className="detail-comment-related-phase-img"
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          );
                                        })}
                                      </span>
                                      <button
                                        type="button"
                                        className="detail-comment-related-remove-btn"
                                        onClick={() => handleDeleteCommentAssociation(row.association_id)}
                                        title="関連を解除"
                                        aria-label="関連を解除"
                                      >
                                        ×
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          {!isNotMyPoster && (
                            <div className="detail-comment-floating-actions">
                              <div className="detail-comment-floating-actions-row">
                                <img
                                  src="/img/edit.png"
                                  alt="編集"
                                  className="detail-edit-icon"
                                  onClick={startEditingComment}
                                />
                                <img
                                  src="/img/gomi.png"
                                  alt="削除"
                                  className="detail-edit-icon gomi"
                                  onClick={() => handleDeleteComment(activeCommentIndex)}
                                />
                              </div>
                              <div className="detail-comment-floating-actions-row detail-comment-floating-actions-row--bottom">
                                <img
                                  src="/img/miso_blue.png"
                                  alt="思考フェーズ取得"
                                  className={`detail-comment-action-icon detail-comment-action-icon--phase${
                                    isPhaseTagsUpToDate(comments[activeCommentIndex])
                                      ? ' detail-comment-action-icon--disabled'
                                      : ''
                                  }`}
                                  title={
                                    isPhaseTagsUpToDate(comments[activeCommentIndex])
                                      ? 'コメントが更新されていないため再取得は不要です'
                                      : '思考フェーズ取得'
                                  }
                                  onClick={
                                    phaseTagsLoading || isPhaseTagsUpToDate(comments[activeCommentIndex])
                                      ? undefined
                                      : confirmFetchCommentPhaseTags
                                  }
                                  style={{
                                    cursor: phaseTagsLoading
                                      ? 'wait'
                                      : isPhaseTagsUpToDate(comments[activeCommentIndex])
                                        ? 'not-allowed'
                                        : 'pointer',
                                    opacity: phaseTagsLoading ? 0.5 : undefined,
                                  }}
                                />
                                <img
                                  src="/img/suggest.png"
                                  alt="関連コメントを提案"
                                  className={`detail-comment-action-icon detail-comment-action-icon--suggest${
                                    Array.isArray(comments[activeCommentIndex]?.phase_tags) &&
                                    comments[activeCommentIndex].phase_tags.length > 0
                                      ? ''
                                      : ' detail-comment-action-icon--disabled'
                                  }`}
                                  title={
                                    Array.isArray(comments[activeCommentIndex]?.phase_tags) &&
                                    comments[activeCommentIndex].phase_tags.length > 0
                                      ? '関連コメントを提案'
                                      : '思考フェーズを取得すると利用できます'
                                  }
                                  onClick={
                                    Array.isArray(comments[activeCommentIndex]?.phase_tags) &&
                                    comments[activeCommentIndex].phase_tags.length > 0
                                      ? openCommentSuggestModal
                                      : undefined
                                  }
                                  style={{
                                    cursor:
                                      Array.isArray(comments[activeCommentIndex]?.phase_tags) &&
                                      comments[activeCommentIndex].phase_tags.length > 0
                                        ? 'pointer'
                                        : 'not-allowed',
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      )}

                      {/* 編集エリア */}
                      {isEditingActiveComment && !isNotMyPoster && (
                        <div className="detail-comment-edit-area">
                          {tempComment.answered_question && (
                            <div className="detail-linked-question">
                              <img src="/img/cat-purple.png" alt="" />
                              <span className="detail-linked-question-text">
                                {tempComment.answered_question}
                              </span>
                            </div>
                          )}
                          <input
                            type="text"
                            className="detail-comment-title"
                            placeholder="タイトルを入力"
                            value={tempComment.title}
                            onChange={(e) => setTempComment({...tempComment, title: e.target.value})}
                          />
                          <textarea 
                            className="detail-comment-body" 
                            rows="4"
                            placeholder="コメントを入力してください"
                            value={tempComment.text}
                            onChange={(e) => setTempComment({...tempComment, text: e.target.value})}
                          ></textarea>
                          {/* タグ編集（最大7個まで） */}
                          <input 
                            type="text" 
                            className="detail-tag-input" 
                            placeholder="タグをカンマで区切って入力（例: タグ1, タグ2, タグ3） 最大7個まで"
                            value={tempTagsText}
                            onChange={(e) => {
                              const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                              if (tags.length <= 7) {
                                setTempTagsText(e.target.value);
                              }
                            }}
                          />
                          <div style={{ color: 'gray', fontSize: '12px', marginTop: '4px' }}>
                            {tempTagsText.split(',').map(tag => tag.trim()).filter(tag => tag).length} / 7 タグ
                          </div>
                          <div className="detail-button-group">
                            <button onClick={cancelEditingComment}>キャンセル</button>
                            <button onClick={handleRegisterComment}>登録</button>

                            {/* モーダルメッセージ */}
                            {modal.show && ReactDOM.createPortal(
                              <div>
                                <div>
                                  <p>{modal.message}</p>
                                  {modal.type === 'confirm' ? (
                                    <div>
                                      <button onClick={modal.onConfirm}>OK</button>
                                      <button onClick={modal.onCancel || closeModal}>キャンセル</button>
                                    </div>
                                  ) : (
                                    <button onClick={modal.onConfirm || closeModal}>OK</button>
                                  )}
                                </div>
                              </div>,
                              document.body
                            )}

                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>

              {/* コメント関連提案モーダル（自分の別コメント / 他ユーザ公開コメント） */}
              {commentSuggestModalOpen && (
                <div
                  className="detail-modal-overlay"
                  role="presentation"
                  onClick={(e) => {
                    if (e.target.classList.contains('detail-modal-overlay')) {
                      closeCommentSuggestModal();
                    }
                  }}
                >
                  <div
                    className="detail-modal-content detail-comment-suggest-modal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button type="button" className="detail-modal-close-button" onClick={closeCommentSuggestModal}>
                      ×
                    </button>
                    {!commentSuggestDetailPoster ? (
                      <>
                        <div className="detail-comment-suggest-tabs">
                          <button
                            type="button"
                            className={commentSuggestTab === 'own' ? 'active' : ''}
                            onClick={() => handleCommentSuggestSwitchTab('own')}
                          >
                            自分の投稿
                          </button>
                          <span className="detail-comment-suggest-tab-sep" aria-hidden>|</span>
                          <button
                            type="button"
                            className={commentSuggestTab === 'other' ? 'active' : ''}
                            onClick={() => handleCommentSuggestSwitchTab('other')}
                          >
                            他ユーザの投稿
                          </button>
                        </div>
                        {commentSuggestLoading ? (
                          <div className="detail-comment-suggest-loading">
                            <div className="loading-spinner" />
                            <p>読み込み中…</p>
                          </div>
                        ) : (
                          <>
                            <ul className="detail-comment-suggest-list">
                              {commentSuggestPayload.items.length === 0 ? (
                                <li className="detail-comment-suggest-empty">提案できるコメントがまだありません</li>
                              ) : (
                                commentSuggestPayload.items.map((row) => {
                                  const linkIcon =
                                    commentSuggestTab === 'own'
                                      ? '/img/link_dblue.png'
                                      : '/img/like_red.png';
                                  return (
                                    <li key={row.doc_id} className="detail-comment-suggest-item">
                                      <div className="detail-comment-suggest-item-head">
                                        <button
                                          type="button"
                                          className="detail-comment-suggest-title-btn"
                                          onClick={() => openCommentSuggestPosterDetail(row.poster_id, row.doc_id)}
                                        >
                                          {row.title}
                                        </button>
                                        {Array.isArray(row.phase_tags) && row.phase_tags.length > 0 && (
                                          <span className="detail-comment-suggest-phase-tags">
                                            {row.phase_tags.map((tagItem, idx) => {
                                              const tk =
                                                tagItem && typeof tagItem === 'object' && tagItem.tag
                                                  ? tagItem.tag
                                                  : typeof tagItem === 'string'
                                                    ? tagItem
                                                    : null;
                                              if (!tk) return null;
                                              return (
                                                <img
                                                  key={idx}
                                                  src={`/img/phase-tags/${tk}.png`}
                                                  alt={tk}
                                                  title={tk}
                                                  className="detail-comment-suggest-phase-img"
                                                  onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                              );
                                            })}
                                          </span>
                                        )}
                                        {SUGGEST_RANKING_ROUTES_WITH_SIMILARITY.includes(
                                          commentSuggestPayload.ranking_route
                                        )
                                          && Number.isInteger(row.similarity_tier)
                                          && row.similarity_tier >= 1
                                          && row.similarity_tier <= 5 && (
                                          <span
                                            className="detail-comment-suggest-similarity"
                                            title="ベクトル検索に基づく相対的な近似の目安（1〜5。5ほどソースに近い想定です。実データにより調整が必要な場合があります）"
                                          >
                                            <span className="detail-comment-suggest-similarity-label">
                                              近似
                                            </span>
                                            <span
                                              className="detail-comment-suggest-similarity-stars"
                                              aria-hidden
                                            >
                                              {Array.from({ length: 5 }, (_, i) => (
                                                <img
                                                  key={i}
                                                  src="/img/dino.png"
                                                  alt=""
                                                  className={
                                                    i < row.similarity_tier
                                                      ? 'detail-comment-suggest-similarity-dino detail-comment-suggest-similarity-dino--on'
                                                      : 'detail-comment-suggest-similarity-dino detail-comment-suggest-similarity-dino--off'
                                                  }
                                                />
                                              ))}
                                            </span>
                                          </span>
                                        )}
                                      </div>
                                      <div className="detail-comment-suggest-item-body">
                                        {row.thumb_url ? (
                                          <img src={row.thumb_url} alt="" className="detail-comment-suggest-thumb" />
                                        ) : (
                                          <div className="detail-comment-suggest-thumb-placeholder" />
                                        )}
                                        <p className="detail-comment-suggest-snippet">
                                          {truncateSuggestSnippet(row.snippet)}
                                        </p>
                                        <button
                                          type="button"
                                          className="detail-comment-suggest-link-btn"
                                          onClick={() => handleCreateCommentAssociation(row)}
                                          disabled={!!row.already_linked}
                                          title={row.already_linked ? '関連付け済み' : '関連付け'}
                                          aria-label={row.already_linked ? '関連付け済み' : '関連付け'}
                                        >
                                          <img src={linkIcon} alt="" className="detail-comment-suggest-link-icon" />
                                        </button>
                                      </div>
                                    </li>
                                  );
                                })
                              )}
                            </ul>
                            {(commentSuggestPayload.total_pages || 1) > 1 && (
                              <div className="detail-comment-suggest-pagination" role="navigation">
                                {Array.from({ length: commentSuggestPayload.total_pages }, (_, i) => i + 1).map(
                                  (pn) => (
                                    <button
                                      key={pn}
                                      type="button"
                                      className={
                                        pn === commentSuggestPage
                                          ? 'detail-comment-suggest-dot active'
                                          : 'detail-comment-suggest-dot'
                                      }
                                      aria-label={`ページ ${pn}`}
                                      onClick={() => handleCommentSuggestPageChange(pn)}
                                    />
                                  )
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <div className="suggest-detail-wrapper">
                        <nav className="detail-breadcrumb suggest-breadcrumb">
                          <button
                            type="button"
                            className="breadcrumb-link"
                            onClick={() => setCommentSuggestDetailPoster(null)}
                          >
                            おすすめ一覧
                          </button>
                          <span className="breadcrumb-separator"> &gt; </span>
                          <span className="breadcrumb-current">おすすめポスター</span>
                        </nav>
                        <PosterDetail isMinimalView poster={commentSuggestDetailPoster} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </React.Fragment>
        )}
      </div>

      {phaseTagsLoading && ReactDOM.createPortal(
        <div
          className="detail-phase-tags-loading-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="detail-phase-tags-loading-panel">
            <img src="/img/cat.png" alt="" className="detail-phase-tags-loading-cat" />
            <p className="detail-phase-tags-loading-text">思考フェーズを取得中…</p>
            <p className="detail-phase-tags-loading-sub">AIがコメントを分析しています</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PosterDetail;
